import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Mic, Sparkles, CheckCircle } from "lucide-react";

const voiceCommands = [
  { text: "Tooth 18, buccal — 3, 2, 3", teeth: [18], site: "buccal", values: [3, 2, 3] },
  { text: "Lingual — 2, 3, 2", teeth: [18], site: "lingual", values: [2, 3, 2] },
  { text: "Bleeding on distal", teeth: [18], bop: ["distal"] },
  { text: "Tooth 19, buccal — 4, 3, 4", teeth: [19], site: "buccal", values: [4, 3, 4] },
  { text: "Bleeding mesial and distal", teeth: [19], bop: ["mesial", "distal"] },
  { text: "Tooth 20 — 5, 4, 5, recession 2", teeth: [20], site: "buccal", values: [5, 4, 5], recession: 2 },
];

const teethData = [
  { num: 18, buccal: [3, 2, 3], lingual: [2, 3, 2], bop: [true, false, true], recession: 0 },
  { num: 19, buccal: [4, 3, 4], lingual: [3, 3, 3], bop: [true, false, true], recession: 0 },
  { num: 20, buccal: [5, 4, 5], lingual: [4, 4, 4], bop: [false, false, false], recession: 2 },
];

const Tooth = ({ tooth, active, shown, showRec }) => {
  const maxPocket = Math.max(...tooth.buccal, ...tooth.lingual);
  const severity = maxPocket >= 5 ? "danger" : maxPocket >= 4 ? "warn" : "ok";
  const color =
    severity === "danger"
      ? "#ef4444"
      : severity === "warn"
        ? "#f59e0b"
        : "#10b981";
  return (
    <motion.div
      initial={{ opacity: 0.3 }}
      animate={{ opacity: shown ? 1 : 0.3, scale: active ? 1.05 : 1 }}
      transition={{ duration: 0.3 }}
      className={`relative flex flex-col items-center gap-0.5 px-2 py-2 rounded-lg ${active ? "bg-blue-50 ring-2 ring-blue-400" : ""}`}
    >
      <p className="text-[10px] font-bold text-slate-400">#{tooth.num}</p>
      {/* Buccal row */}
      <div className="flex gap-1.5">
        {tooth.buccal.map((v, i) => (
          <AnimatePresence key={i}>
            {shown ? (
              <motion.span
                initial={{ opacity: 0, y: -5 }}
                animate={{ opacity: 1, y: 0 }}
                key={v + "-" + i}
                className="w-4 text-center text-[11px] font-bold tabular-nums"
                style={{ color: v >= 5 ? "#dc2626" : v >= 4 ? "#d97706" : "#0f766e" }}
              >
                {v}
              </motion.span>
            ) : (
              <span className="w-4 text-center text-[11px] text-slate-300">—</span>
            )}
          </AnimatePresence>
        ))}
      </div>
      {/* Tooth glyph */}
      <svg viewBox="0 0 28 36" className="w-7 h-9">
        <defs>
          <linearGradient id={`tg-${tooth.num}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#f8fafc" />
            <stop offset="100%" stopColor="#e2e8f0" />
          </linearGradient>
        </defs>
        <path
          d="M14 2 C8 2, 4 6, 4 12 L4 20 C4 26, 7 32, 10 34 L18 34 C21 32, 24 26, 24 20 L24 12 C24 6, 20 2, 14 2 Z"
          fill={`url(#tg-${tooth.num})`}
          stroke={shown ? color : "#cbd5e1"}
          strokeWidth={shown ? 1.5 : 1}
        />
        {shown && tooth.bop.some((b) => b) && (
          <circle cx="22" cy="8" r="3" fill="#ef4444" />
        )}
      </svg>
      {/* Lingual row */}
      <div className="flex gap-1.5">
        {tooth.lingual.map((v, i) => (
          <AnimatePresence key={i}>
            {shown ? (
              <motion.span
                initial={{ opacity: 0, y: 5 }}
                animate={{ opacity: 1, y: 0 }}
                key={v + "-" + i}
                className="w-4 text-center text-[10px] text-slate-500 tabular-nums"
              >
                {v}
              </motion.span>
            ) : (
              <span className="w-4 text-center text-[10px] text-slate-300">—</span>
            )}
          </AnimatePresence>
        ))}
      </div>
      {/* Recession badge */}
      {shown && tooth.recession > 0 && showRec && (
        <motion.span
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-[9px] font-bold text-rose-500 tabular-nums"
        >
          Rec {tooth.recession}
        </motion.span>
      )}
    </motion.div>
  );
};

const VoicePerioSlide = () => {
  const [step, setStep] = useState(0);
  const [typed, setTyped] = useState("");
  const [activeTooth, setActiveTooth] = useState(null);
  const [bubbles, setBubbles] = useState([]);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      while (!cancelled) {
        setStep(0);
        setBubbles([]);
        setTyped("");
        setActiveTooth(null);
        await new Promise((r) => setTimeout(r, 1200));

        for (let i = 0; i < voiceCommands.length; i++) {
          const cmd = voiceCommands[i];
          if (cancelled) return;

          // Type out the voice command
          setActiveTooth(cmd.teeth[0]);
          setTyped("");
          for (let c = 0; c <= cmd.text.length; c += 2) {
            if (cancelled) return;
            setTyped(cmd.text.slice(0, c));
            await new Promise((r) => setTimeout(r, 22));
          }
          setTyped(cmd.text);
          await new Promise((r) => setTimeout(r, 300));

          // Persist bubble
          setBubbles((b) => [...b, cmd.text].slice(-4));
          setTyped("");

          // Advance "shown" state by 1 if moving to next tooth, otherwise partial
          setStep(Math.min(i + 1, voiceCommands.length));
          await new Promise((r) => setTimeout(r, 500));
        }

        setActiveTooth(null);
        await new Promise((r) => setTimeout(r, 3500));
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, []);

  // How many teeth to show fully charted
  const teethShown = step >= 6 ? 3 : step >= 4 ? 2 : step >= 1 ? 1 : 0;

  return (
    <div className="h-full w-full bg-[#f8fafc] flex flex-col items-center justify-center p-8 xl:p-12 relative overflow-hidden font-sans">
      {/* Ambient gradient blurs */}
      <div
        className="absolute top-[-200px] right-[-100px] w-[700px] h-[700px] rounded-full blur-[120px] opacity-30 pointer-events-none"
        style={{ background: "linear-gradient(135deg, #bfdbfe 0%, #93c5fd 50%, #dbeafe 100%)" }}
      />
      <div
        className="absolute bottom-[-200px] left-[-100px] w-[600px] h-[600px] rounded-full blur-[120px] opacity-25 pointer-events-none"
        style={{ background: "linear-gradient(135deg, #a5f3fc 0%, #93c5fd 50%, #bfdbfe 100%)" }}
      />
      <div className="absolute inset-0 bg-[radial-gradient(#cbd5e1_0.8px,transparent_0.8px)] [background-size:28px_28px] opacity-40 pointer-events-none" />

      <div className="w-full flex flex-col items-center relative z-10 max-w-[1200px]">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-6"
        >
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-blue-50 text-blue-600 text-sm font-semibold mb-3 border border-blue-100/60 shadow-sm">
            <Mic className="w-4 h-4" />
            <span>PMS Feature — Voice Perio</span>
          </div>
          <h2 className="text-3xl lg:text-[42px] font-bold text-slate-900 tracking-tight leading-tight">
            Chart a full perio exam —{" "}
            <span className="bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
              with your voice.
            </span>
          </h2>
          <p className="text-slate-900 text-[22px] font-semibold mt-6 max-w-2xl mx-auto leading-relaxed">
            Hands on the patient, eyes on the mouth. Say the numbers — Asha fills the chart in
            real time.
          </p>
        </motion.div>

        {/* Main demo card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15, duration: 0.5 }}
          className="w-full grid grid-cols-[1fr_1.4fr] gap-5"
        >
          {/* LEFT — Voice panel */}
          <div className="rounded-[20px] bg-white border border-slate-200 shadow-[0_8px_60px_-12px_rgba(0,0,0,0.08)] p-5 flex flex-col">
            {/* Mic header */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <div className="relative">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-lg shadow-blue-500/30">
                    <Mic className="w-4 h-4 text-white" />
                  </div>
                  <motion.div
                    animate={{ scale: [1, 1.5, 1], opacity: [0.6, 0, 0.6] }}
                    transition={{ duration: 1.8, repeat: Infinity }}
                    className="absolute inset-0 rounded-full border-2 border-blue-400"
                  />
                </div>
                <div>
                  <p className="text-[13px] font-bold text-slate-900">Listening…</p>
                  <p className="text-[10px] text-slate-400">Hands-free perio charting</p>
                </div>
              </div>
              <span className="text-[10px] font-bold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full border border-emerald-100 flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                LIVE
              </span>
            </div>

            {/* Live transcript */}
            <div className="bg-slate-50 rounded-xl border border-slate-100 p-4 min-h-[88px] mb-3">
              <p className="text-[9.5px] font-bold text-slate-400 uppercase tracking-wider mb-1.5">
                Current
              </p>
              <p className="text-[15px] font-semibold text-slate-800 leading-snug min-h-[40px]">
                {typed}
                {typed && <span className="inline-block w-[1.5px] h-4 bg-blue-500 ml-0.5 animate-pulse align-middle" />}
                {!typed && (
                  <span className="text-slate-300 italic font-normal text-[13px]">
                    Speak to chart…
                  </span>
                )}
              </p>
            </div>

            {/* Recent bubbles */}
            <div>
              <p className="text-[9.5px] font-bold text-slate-400 uppercase tracking-wider mb-2">
                Just captured
              </p>
              <div className="space-y-1.5">
                <AnimatePresence mode="popLayout">
                  {bubbles.slice().reverse().map((b, i) => (
                    <motion.div
                      key={b + i}
                      initial={{ opacity: 0, x: -12 }}
                      animate={{ opacity: 1 - i * 0.22, x: 0 }}
                      exit={{ opacity: 0, x: 12 }}
                      transition={{ duration: 0.3 }}
                      className="flex items-center gap-2 bg-blue-50/60 border border-blue-100 rounded-lg px-2.5 py-1.5"
                    >
                      <CheckCircle className="w-3 h-3 text-blue-500 shrink-0" />
                      <span className="text-[11.5px] text-slate-700 font-medium truncate">
                        {b}
                      </span>
                    </motion.div>
                  ))}
                </AnimatePresence>
              </div>
            </div>

            {/* Capability tags */}
            <div className="mt-auto pt-4 flex flex-wrap gap-1.5">
              {[
                "Probe depths",
                "Bleeding (BOP)",
                "Recession",
                "Mobility",
                "Furcation",
              ].map((t) => (
                <span
                  key={t}
                  className="text-[10px] font-semibold text-slate-500 bg-slate-50 border border-slate-100 rounded-full px-2 py-0.5"
                >
                  {t}
                </span>
              ))}
            </div>
          </div>

          {/* RIGHT — Perio chart */}
          <div className="rounded-[20px] bg-white border border-slate-200 shadow-[0_8px_60px_-12px_rgba(0,0,0,0.08)] p-5">
            <div className="flex items-center justify-between mb-3">
              <div>
                <p className="text-[9.5px] font-bold text-slate-400 uppercase tracking-wider">
                  Perio Chart
                </p>
                <p className="text-[13px] font-bold text-slate-900">Lower left quadrant</p>
              </div>
              <div className="flex items-center gap-1.5 text-[9.5px] font-semibold">
                <span className="flex items-center gap-1">
                  <span className="w-2 h-2 rounded-full bg-emerald-500" />
                  <span className="text-slate-500">Healthy</span>
                </span>
                <span className="flex items-center gap-1">
                  <span className="w-2 h-2 rounded-full bg-amber-500" />
                  <span className="text-slate-500">4mm+</span>
                </span>
                <span className="flex items-center gap-1">
                  <span className="w-2 h-2 rounded-full bg-red-500" />
                  <span className="text-slate-500">5mm+</span>
                </span>
              </div>
            </div>

            <div className="rounded-xl bg-slate-50 border border-slate-100 p-4">
              <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mb-1">
                Buccal
              </p>
              <div className="flex justify-center gap-2">
                {teethData.map((t, i) => (
                  <Tooth
                    key={t.num}
                    tooth={t}
                    shown={i < teethShown}
                    active={activeTooth === t.num}
                    showRec={step >= 6}
                  />
                ))}
              </div>
              <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mt-1 text-right">
                Lingual
              </p>
            </div>

            {/* Stage indicator */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: step >= 6 ? 1 : 0 }}
              transition={{ duration: 0.5 }}
              className="mt-3 rounded-xl bg-gradient-to-br from-rose-50 to-red-50 border border-rose-100 px-4 py-2.5 flex items-center justify-between"
            >
              <div className="flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-rose-500" />
                <span className="text-[12px] font-semibold text-slate-700">
                  AI flagged:
                </span>
                <span className="text-[12px] text-slate-800">
                  Tooth #20 — Stage III perio, SRP indicated
                </span>
              </div>
              <span className="text-[10px] font-bold text-rose-600 bg-white/80 border border-rose-100 px-2 py-0.5 rounded-full">
                D4341 suggested
              </span>
            </motion.div>
          </div>
        </motion.div>

        {/* Bottom stat strip */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.5 }}
          className="w-full mt-4 grid grid-cols-3 gap-3"
        >
          {[
            { label: "Charting time", value: "↓ 60%", sub: "vs typing" },
            { label: "Single-operator exams", value: "Yes", sub: "No assistant needed" },
            { label: "Auto Stage/Grade", value: "AAP 2017", sub: "Perio classification" },
          ].map((s) => (
            <div
              key={s.label}
              className="rounded-xl bg-white/80 border border-slate-200 px-4 py-2.5 flex items-center justify-between"
            >
              <div>
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                  {s.label}
                </p>
                <p className="text-[10px] text-slate-500">{s.sub}</p>
              </div>
              <p className="text-[18px] font-extrabold tracking-tight bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                {s.value}
              </p>
            </div>
          ))}
        </motion.div>
      </div>
    </div>
  );
};

export default VoicePerioSlide;
